import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Slide,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import EditLocationAltIcon from "@mui/icons-material/EditLocationAlt";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import useTranslation from "../../../../shared/hooks/useTranslation";
import AddressAutocomplete, {
  AddressData,
} from "../../../../shared/components/AddressAutocomplete";
import { dt, accentGradient } from "../../../../shared/themes/redesignTokens";

export interface DepartureLocation {
  lat: number;
  lng: number;
  label: string;
}

interface DeparturePointModalProps {
  open: boolean;
  onClose: () => void;
  currentDeparture: DepartureLocation | null;
  companyAddress: DepartureLocation | null;
  onDepartureChange: (location: DepartureLocation) => void;
}

const SlideUp = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const DeparturePointModal = ({
  open,
  onClose,
  currentDeparture,
  companyAddress,
  onDepartureChange,
}: DeparturePointModalProps) => {
  const { translate } = useTranslation(["serviceOffers"]);

  const [pendingLocation, setPendingLocation] =
    useState<DepartureLocation | null>(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsAcquired, setGpsAcquired] = useState(false);
  const [gpsError, setGpsError] = useState<string | null>(null);

  const handleUseGps = () => {
    if (!navigator.geolocation) {
      setGpsError(translate("departure.gpsNotSupported"));
      return;
    }

    setGpsLoading(true);
    setGpsError(null);
    setGpsAcquired(false);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const loc: DepartureLocation = {
          lat: Math.round(position.coords.latitude * 10000) / 10000,
          lng: Math.round(position.coords.longitude * 10000) / 10000,
          label: translate("departure.currentLocation", {
            lat: position.coords.latitude.toFixed(4),
            lng: position.coords.longitude.toFixed(4),
          }),
        };
        setPendingLocation(loc);
        setGpsLoading(false);
        setGpsAcquired(true);
      },
      (err) => {
        setGpsLoading(false);
        switch (err.code) {
          case err.PERMISSION_DENIED:
            setGpsError(translate("departure.gpsPermissionDenied"));
            break;
          case err.POSITION_UNAVAILABLE:
            setGpsError(translate("departure.gpsUnavailable"));
            break;
          case err.TIMEOUT:
            setGpsError(translate("departure.gpsTimeout"));
            break;
          default:
            setGpsError(translate("departure.gpsUnknownError"));
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const handleAddressChange = (address?: AddressData) => {
    if (address?.latitude && address?.longitude) {
      setPendingLocation({
        lat: address.latitude,
        lng: address.longitude,
        label: translate("departure.customAddress"),
      });
    }
  };

  const handleResetToCompany = () => {
    if (companyAddress) {
      setPendingLocation({ ...companyAddress });
    }
  };

  const handleApply = () => {
    if (pendingLocation) {
      onDepartureChange(pendingLocation);
    }
    handleClose();
  };

  const handleClose = () => {
    setPendingLocation(null);
    setGpsLoading(false);
    setGpsAcquired(false);
    setGpsError(null);
    onClose();
  };

  const effectiveLabel =
    pendingLocation?.label || currentDeparture?.label || translate("departure.companyDefault");

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      TransitionComponent={SlideUp}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: dt.radius,
          overflow: "hidden",
        },
      }}
    >
      {/* Accent bar */}
      <Box
        sx={{
          height: 4,
          background: accentGradient,
        }}
      />

      <DialogContent sx={{ p: 3 }}>
        {/* Title */}
        <Box
          sx={{
            fontFamily: dt.font,
            fontSize: "18px",
            fontWeight: 700,
            color: dt.textPrimary,
            mb: 0.5,
          }}
        >
          {translate("departure.title")}
        </Box>
        <Box
          sx={{
            fontFamily: dt.font,
            fontSize: "13px",
            color: dt.textSecondary,
            mb: 3,
            lineHeight: 1.5,
          }}
        >
          {translate("departure.subtitle")}
        </Box>

        {/* Current departure */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2.5,
            p: 1.5,
            borderRadius: dt.radiusSm,
            backgroundColor: dt.blueSoft,
            border: `1px solid ${dt.blueGlow}`,
          }}
        >
          <EditLocationAltIcon
            sx={{ fontSize: 18, color: dt.blue, flexShrink: 0 }}
          />
          <Box>
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "11px",
                fontWeight: 500,
                color: dt.textSecondary,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              {translate("departure.currentDeparture")}
            </Box>
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 600,
                color: dt.textPrimary,
              }}
            >
              {effectiveLabel}
            </Box>
          </Box>
        </Box>

        {/* GPS Button */}
        <Box
          component="button"
          type="button"
          onClick={handleUseGps}
          disabled={gpsLoading}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            width: "100%",
            p: 1.5,
            mb: 1.5,
            background: dt.cardBg,
            border: `1px solid ${dt.cardBorder}`,
            borderRadius: dt.radiusSm,
            cursor: gpsLoading ? "wait" : "pointer",
            transition: "all 0.2s",
            "&:hover": {
              borderColor: dt.blue,
              boxShadow: `0 0 0 2px ${dt.blueGlow}`,
            },
          }}
        >
          {gpsLoading ? (
            <CircularProgress size={18} sx={{ color: dt.blue }} />
          ) : gpsAcquired ? (
            <CheckCircleIcon sx={{ fontSize: 18, color: "#10B981" }} />
          ) : (
            <MyLocationIcon sx={{ fontSize: 18, color: dt.blue }} />
          )}
          <Box sx={{ flex: 1, textAlign: "left" }}>
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 600,
                color: dt.textPrimary,
              }}
            >
              {gpsLoading
                ? translate("departure.acquiringPosition")
                : gpsAcquired
                ? translate("departure.positionAcquired")
                : translate("departure.useCurrentLocation")}
            </Box>
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "11px",
                color: dt.textMuted,
              }}
            >
              {translate("departure.useGpsHint")}
            </Box>
          </Box>
        </Box>

        {/* GPS Error */}
        {gpsError && (
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "12px",
              color: "#DC2626",
              mb: 1.5,
              pl: 1,
            }}
          >
            {gpsError}
          </Box>
        )}

        {/* Address input */}
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "12px",
              fontWeight: 500,
              color: dt.textSecondary,
              mb: 0.75,
            }}
          >
            {translate("departure.typeAddress")}
          </Box>
          <AddressAutocomplete
            placeholder={translate("departure.addressPlaceholder")}
            onChange={handleAddressChange}
          />
        </Box>

        {/* Reset to company */}
        {companyAddress && (
          <Box
            component="button"
            type="button"
            onClick={handleResetToCompany}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              background: "none",
              border: "none",
              cursor: "pointer",
              fontFamily: dt.font,
              fontSize: "12px",
              fontWeight: 500,
              color: dt.textSecondary,
              p: 0,
              "&:hover": { color: dt.blue },
            }}
          >
            <RestartAltIcon sx={{ fontSize: 16 }} />
            {translate("departure.resetToCompany")}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          onClick={handleClose}
          sx={{
            fontFamily: dt.font,
            fontSize: "13px",
            fontWeight: 600,
            textTransform: "none",
            color: dt.textSecondary,
          }}
        >
          {translate("common:cancel")}
        </Button>
        <Button
          onClick={handleApply}
          disabled={!pendingLocation}
          variant="contained"
          sx={{
            fontFamily: dt.font,
            fontSize: "13px",
            fontWeight: 600,
            textTransform: "none",
            borderRadius: dt.radiusSm,
            px: 3,
            background: accentGradient,
            "&:hover": {
              background: accentGradient,
              filter: "brightness(1.05)",
            },
            "&:disabled": {
              background: "#E5E7EB",
              color: "#9CA3AF",
            },
          }}
        >
          {translate("departure.apply")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeparturePointModal;
